/** Part VI — Settings and tools: options, controllers and touch, VR, and the admin / debug tools. */
import type { HelpPart } from './types.js';

export const PART_SETTINGS: HelpPart = {
  numeral: 'VI',
  title: 'Settings and tools',
  chapters: [
    {
      id: 'options',
      title: 'Options',
      blurb: 'Graphics and the Apply button, sound, controls and rebinding, controllers, touch, themes and comfort.',
      pages: [
        {
          id: 'options/menu',
          title: 'The Options menu',
          terms: ['options', 'settings', 'esc', 'general', 'tabs'],
          body: `<p><kbd>Esc</kbd> (or <b>Options</b> on the menu bar) opens four tabs: <b>General</b> (your account, the
          interface theme, translation), <b>Graphics</b>, <b>Sound</b> and <b>Controls</b>.</p>
          <p>Sound and controls change the moment you set them. Graphics changes wait for <b>Apply</b> (next page). Every
          graphics row has a <b>?</b> that explains in plain words what it changes, what it costs, and when it takes
          effect.</p>`,
        },
        {
          id: 'options/graphics',
          title: 'Graphics and the Apply button',
          terms: ['graphics', 'apply', 'discard', 'draft', 'quality', 'restart'],
          body: `<p>Change as many graphics rows as you like: each changed row is marked and says what it was. The bar at the
          foot counts your changes; <b>Apply</b> rebuilds the picture once with all of them, <b>Discard</b> throws them away.
          Closing the menu keeps your changes waiting (a dot on the Graphics tab says so).</p>
          <p>A few rows say they take effect <b>after a reload</b>, <b>at the next world</b> or <b>in space</b> — Apply sets
          them, but they need that moment to show.</p>`,
        },
        {
          id: 'options/presets',
          title: 'Quality presets and performance',
          terms: ['preset', 'minimal', 'potato', 'low', 'medium', 'high', 'ultra', 'fps', 'auto-tune', 'resolution', 'upscaling', 'draw distance'],
          body: `<p>Six presets, from <b>Minimal</b> to <b>Ultra</b>, set everything at once; change any row afterwards to make
          your own. Higher presets add shadows, clouds, reflections, ambient occlusion, more and closer detail, sharper
          textures and a higher render resolution.</p>
          <ul>
          <li><b>Resolution</b> — each preset aims at a target (480p on Minimal up to 4K on Ultra); <b>Upscaling</b> renders
          below it and sharpens up, which costs far less than rendering every pixel.</li>
          <li><b>Adaptive resolution</b> — lowers the render scale for a moment when frames run long, and creeps back up
          when there is headroom.</li>
          <li><b>Auto-tune</b> — steps the whole preset down if the frame rate stays low.</li>
          <li><b>Draw distance</b> — how far the ground and towns are built (500 m up); beyond it, simplified distant towns
          and terrain fill the horizon unless <b>Distant detail</b> is off.</li>
          </ul>
          <p><kbd>F3</kbd> shows the frame rate and what is being drawn.</p>`,
        },
        {
          id: 'options/sound',
          title: 'Sound',
          terms: ['sound', 'volume', 'music', 'headphones', 'captions', 'night mode', 'versona voices'],
          body: `<ul>
          <li><b>Volumes</b> — master and each category: music; effects (combat, vehicles, footsteps, machines); ambience
          and weather; interface; dialogue (townsfolk voices); voice chat; cinematics. Changes play live.</li>
          <li><b>Output</b> — headphones, speakers or mono.</li>
          <li><b>Range</b> — full, or night mode (quiet sounds lifted, loud ones tamed).</li>
          <li><b>Room acoustics</b> — rooms and caves echo.</li>
          <li><b>Sound captions</b> — on-screen captions for important sounds, with the direction they came from.</li>
          <li><b>Townsfolk voices</b> — whether townsfolk speak their lines aloud, and how loud.</li>
          <li><b>Speaker check</b> — plays a sound from each direction so you can tell left from right.</li>
          </ul>`,
        },
        {
          id: 'options/controls',
          title: 'Controls and rebinding',
          terms: ['rebind', 'keybinding', 'controls', 'key', 'conflict'],
          body: `<p>The Controls tab lists every action with its key and its controller button. Click a key, then press the new
          one; <kbd>Esc</kbd> cancels. A key that already means something else in the same situation is refused, and the
          clash is named. A few keys are fixed and cannot be taken (<kbd>H</kbd>, <kbd>Enter</kbd>, the function keys).</p>
          <p>The same key can mean different things in different situations — walking, riding, flying, in space — without
          clashing. Every page of this book and every prompt follows your bindings. Reset puts the defaults back.</p>`,
        },
        {
          id: 'options/gamepad',
          title: 'Playing with a controller',
          terms: ['gamepad', 'controller', 'xbox', 'playstation', 'rumble'],
          body: `<p>Plug in a controller and press a button. Everything in Cosmos can be played with one.</p>
          <table>
          <tr><td>Left / right stick</td><td>Move / look</td></tr>
          <tr><td>A · B · X</td><td>Jump · crouch · interact</td></tr>
          <tr><td>Y or RB · LB</td><td>Next / previous weapon</td></tr>
          <tr><td>RT · LT</td><td>Attack · guard</td></tr>
          <tr><td>Left stick click</td><td>Sprint</td></tr>
          <tr><td>D-pad</td><td>Zoom in / out, auto-run (left), inventory (right)</td></tr>
          <tr><td>Start · Select</td><td>Options · swap worlds</td></tr>
          </table>
          <p>Riding, the triggers are throttle and brake and the left stick steers. Flying, the left stick is throttle and roll
          and the triggers fire and burn. In menus the d-pad moves between buttons, A presses, B goes back or closes the top
          window, the shoulders switch tabs and the right stick scrolls. Every button can be rebound in Controls; rumble can be
          turned down or off.</p>`,
        },
        {
          id: 'options/touch',
          title: 'Touch controls',
          terms: ['touch', 'mobile', 'tablet', 'on-screen buttons'],
          body: `<p>On a touch screen, on-screen sticks and buttons appear. Options → <b>Touch controls</b> adds and removes
          buttons, and <b>Arrange</b> lets you drag and resize them where your thumbs fall. Positions are kept relative to the
          screen, so a layout survives turning the device.</p>`,
        },
        {
          id: 'options/themes',
          title: 'Interface themes',
          terms: ['theme', 'frontier', 'borean', 'aurelian', 'interface'],
          body: `<p>Options → General → <b>Interface theme</b>: <b>Frontier</b> (burnt umber, brass and rust — the default),
          <b>Borean</b> (glacier slate and ice, frosted glass) or <b>Aurelian</b> (lacquer and gold leaf). Every window, this
          book included, changes at once. The two peoples' colours stay the same in every theme.</p>`,
        },
        {
          id: 'options/comfort',
          title: 'Camera motion and comfort',
          terms: ['motion sickness', 'camera shake', 'reduced motion', 'comfort'],
          body: `<p>Options → Controls → <b>Camera motion</b>: <b>System</b> (follows your device's reduced-motion setting),
          <b>Full</b>, <b>Reduced</b> or <b>Off</b>. It scales the extra camera movement — hit kicks, shake, field-of-view
          kicks, chase-camera sway — never your aim. <b>Camera shake</b> is a separate switch.</p>
          <p>In flight, “Fly the sector like the sky” keeps space flight feeling like flying in the atmosphere.</p>`,
        },
      ],
    },
    {
      id: 'vr',
      title: 'Virtual reality',
      blurb: 'Entering VR, moving comfortably, and the panels in the headset.',
      pages: [
        {
          id: 'vr/enter',
          title: 'Entering VR',
          terms: ['vr', 'quest', 'headset', 'webxr'],
          body: `<p>In a VR-capable browser (Meta Quest's, or a desktop headset) an <b>Enter VR</b> button appears once you
          are in a world. The game picks settings for your headset; you can choose a quality level in the headset's
          comfort panel. Your desktop settings are kept exactly as they were when you take the headset off.</p>`,
        },
        {
          id: 'vr/move',
          title: 'Moving in VR',
          terms: ['teleport', 'snap turn', 'smooth turn', 'vignette', 'seated'],
          body: `<ul>
          <li>The <b>move-hand stick</b> walks you where you look (choose which hand).</li>
          <li><b>Turning</b> — snap turns or smooth turns.</li>
          <li><b>Teleport</b> — point and release to hop a short way.</li>
          <li><b>Vignette</b> — the view narrows while the game moves you, which eases motion sickness. Turning your own head
          never triggers it.</li>
          <li><b>Seated</b> mode and <b>recenter</b> adjust your height and forward.</li>
          </ul>
          <p>On a hovercraft the triggers are throttle and brake and the stick steers.</p>`,
        },
        {
          id: 'vr/panels',
          title: 'Panels in VR',
          terms: ['vr menu', 'wrist menu', 'panel', 'hand tracking'],
          body: `<p>Press <b>X</b> on the left controller (or tap the orb where there is no X) for the in-headset panel: comfort,
          performance, chat, inventory, map and conversations. Point with either controller and pull the trigger, or pinch with
          tracked hands. A small menu also rides on your wrist.</p>`,
        },
      ],
    },
    {
      id: 'admin',
      title: 'Admin and debug tools',
      blurb: 'For administrators and local development: the world editor, the debug console and its commands.',
      pages: [
        {
          id: 'admin/editor',
          title: 'The admin world editor',
          terms: ['admin', 'fly camera', 'palette', 'placement', 'outliner'],
          body: `<p>Administrators of the Commons can hand-design its cities. <code>/fly</code> enters a fly camera (WASD
          along your view, Space and Ctrl up and down, Shift faster); while flying, your character stands still for everyone
          else. <kbd>B</kbd> opens the prefab palette; aim at the ground and click to place.</p>
          <table>
          <tr><td>Click</td><td>Place, or select a placement</td></tr>
          <tr><td><kbd>1</kbd> <kbd>2</kbd> <kbd>3</kbd></td><td>Move, rotate, scale (or type exact numbers)</td></tr>
          <tr><td><kbd>X</kbd></td><td>Snapping</td></tr>
          <tr><td><kbd>Shift</kbd>+<kbd>D</kbd> · <kbd>Ctrl</kbd>+<kbd>C</kbd>/<kbd>V</kbd></td><td>Duplicate · copy and paste at the crosshair</td></tr>
          <tr><td><kbd>O</kbd></td><td>Outliner of every placement</td></tr>
          <tr><td><kbd>Ctrl</kbd>+<kbd>Z</kbd> · <kbd>Delete</kbd></td><td>Undo · delete</td></tr>
          </table>
          <p>Every change is checked and saved by the server, and everyone sees it. Edit Mode (<kbd>F2</kbd>) does the same work
          with more tools.</p>`,
        },
        {
          id: 'admin/console',
          title: 'The debug console',
          terms: ['debug', 'console', 'backtick', 'teleport', 'tp', 'grant admin'],
          body: `<p><kbd>\`</kbd> (backtick) opens the debug panel on a local or development build. It has a command console,
          weather and time controls (just for you, or for the whole Commons), terrain-feature teleports, the bounty ledger and
          a <b>Probes…</b> window of measurements.</p>
          <table>
          <tr><td><code>/tp x z</code> · <code>/tp-home</code></td><td>Teleport</td></tr>
          <tr><td><code>/tp-city name</code> · <code>/tp-settlement id</code> · <code>/tp-biome biome</code></td><td>Go to a place</td></tr>
          <tr><td><code>/list-cities</code> · <code>/list-settlements</code> · <code>/list-biomes</code></td><td>Lists with ids and distances</td></tr>
          <tr><td><code>/name-city id name</code></td><td>Rename a settlement (admin)</td></tr>
          <tr><td><code>/fly</code> · <code>/grant-admin</code> · <code>/help</code></td><td>Fly camera · claim the first admin seat · all commands</td></tr>
          </table>
          <p>Debug mode only shows tools; it grants no power on the server. Administration is a server role.</p>`,
        },
      ],
    },
  ],
};
